import { authService } from "../auth/services/authService";
import { ApiError, type RequestOptions } from "./apiTypes";

const CLIENT_NAME = "FroppioForms";

export const createApiService = (controllerPath: string) => {
  const baseURL = import.meta.env.VITE_API_URL ?? "http://localhost:3000/api";

  const request = async <T>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
    const { skipAuth = false, headers, ...restOptions } = options;
    const requestHeaders = new Headers(headers);
    requestHeaders.set("Content-Type", "application/json");
    requestHeaders.set("X-Client-Id", CLIENT_NAME);

    if (!skipAuth) {
      const token = await authService.getAccessToken();
      if (!token) throw new ApiError("Sign in to continue.", 401);
      requestHeaders.set("Authorization", `Bearer ${token}`);
    }

    const path = `${controllerPath}/${endpoint}`.replace(/\/\/+/g, "/");
    const response = await fetch(`${baseURL.replace(/\/+$/, "")}/${path}`, {
      ...restOptions,
      headers: requestHeaders,
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData: unknown;
      try {
        errorData = await response.json();
        if (errorData && typeof errorData === "object") {
          if ("error" in errorData && typeof errorData.error === "string") {
            errorMessage = errorData.error;
          } else if ("message" in errorData && typeof errorData.message === "string") {
            errorMessage = errorData.message;
          }
        }
      } catch {
        // A non-JSON error still surfaces its HTTP status.
      }
      throw new ApiError(errorMessage, response.status, errorData);
    }

    return response.json();
  };

  return {
    get: <T>(endpoint: string, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "GET" }),
    post: <T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "POST", body: JSON.stringify(data) }),
    put: <T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "PUT", body: JSON.stringify(data) }),
    patch: <T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "PATCH", body: JSON.stringify(data) }),
    delete: <T>(endpoint: string, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "DELETE" }),
  };
};

export type ApiServiceType = ReturnType<typeof createApiService>;
